import * as React from 'react';

import { cn } from '../../lib/utils';

export interface CheckboxProps {
  /** Whether the checkbox is selected. Controlled. */
  checked: boolean;
  /** Callback fired when the checkbox's checked state changes. */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Visible label rendered next to the visual square. */
  label: string;
  /** `name` attribute forwarded to the underlying input. */
  name?: string;
  /** `value` attribute forwarded to the underlying input. */
  value?: string;
  /** Disables the checkbox. Blocks click and keyboard activation. */
  disabled?: boolean;
  /** Optional className appended to the root wrapper. */
  className?: string;
}

/**
 * Checkbox atom — Default / Hover / Selected states per Figma `289:50641`.
 *
 * Wraps a visually-hidden native `<input type="checkbox">` so keyboard
 * navigation (Tab / Space) and screen-reader semantics are preserved. Visual
 * square is 12×12 inside a 16×16 outer touch target with `rounded-[4px]`.
 * Selected state renders a dawn-orange filled square + white check glyph.
 *
 * Indeterminate and Disabled visuals are not in Figma yet — tracked in
 * canonicalization-map § 5. The `disabled` prop is supported at the input
 * level (a11y) and applies an opacity reduction to the wrapper.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ checked, onChange, label, name, value, disabled = false, className }, ref) => {
    return (
      <label
        className={cn(
          'inline-flex items-center gap-2 font-outfit text-[13px] leading-[1.3] text-dawn-black',
          disabled && 'cursor-not-allowed opacity-50',
          !disabled && 'cursor-pointer',
          className
        )}
      >
        <span
          data-checkbox-outer
          className="relative inline-flex h-4 w-4 items-center justify-center"
        >
          <input
            ref={ref}
            type="checkbox"
            name={name}
            value={value}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            aria-checked={checked}
            className="peer absolute inset-0 h-full w-full cursor-[inherit] opacity-0"
          />
          <span
            data-checkbox-square
            className={cn(
              'inline-flex h-3 w-3 items-center justify-center rounded-[4px] border transition-colors',
              checked
                ? 'border-dawn-orange bg-dawn-orange'
                : 'border-grey-400 bg-transparent hover:border-dawn-orange peer-hover:border-dawn-orange peer-focus-visible:border-dawn-orange'
            )}
          >
            {checked && (
              <svg
                data-checkbox-check
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                aria-hidden="true"
              >
                <path
                  d="M2.5 6.25L4.83333 8.5L9.5 3.75"
                  stroke="#FFFFFF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        </span>
        <span>{label}</span>
      </label>
    );
  }
);
Checkbox.displayName = 'Checkbox';
